import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CompanyFilter } from '../components/CompanyFilter';
import { useDateRange } from '../hooks/useDateRange';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Info, Layers, Hammer } from 'lucide-react';

function MetricTip({ text }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild><Info size={12} className="inline ml-1 text-slate-400 cursor-help" /></TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs"><p>{text}</p></TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ConfBadge({ level }) {
  const cls = level === 'high' ? 'bg-emerald-50 text-emerald-600' : level === 'medium' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-400';
  return <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>{level}</span>;
}

export default function StrataComparisonPage() {
  const [strataData, setStrataData] = useState({ strata: [] });
  const [typeData, setTypeData] = useState({ types: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('strata');
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterSite, setFilterSite] = useState('all');
  const { dateFrom, dateTo, setDateFrom, setDateTo, dateParams } = useDateRange();

  const fetchData = () => {
    const params = {};
    if (filterCompany !== 'all') params.company_id = filterCompany;
    if (filterSite !== 'all') params.site_id = filterSite;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const qs = new URLSearchParams(params).toString();
    Promise.all([
      api.get(`/analytics/strata-comparison?${qs}`).catch(() => ({ data: { strata: [] } })),
      api.get(`/analytics/type-comparison?${qs}`).catch(() => ({ data: { types: [] } })),
    ]).then(([s, t]) => { setStrataData(s.data); setTypeData(t.data); }).finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get('/companies').then(r => setCompanies(r.data)).catch(() => {});
    api.get('/sites').then(r => setSites(r.data)).catch(() => {});
    fetchData();
  }, []);
  useEffect(() => { setLoading(true); fetchData(); }, [filterCompany, filterSite, dateFrom, dateTo]);

  const strataChart = (strataData.strata || []).filter(s => s.cost_per_hour > 0).map(s => ({ name: s.strata_label, cph: s.cost_per_hour, spend: s.total_spend }));
  const typeChart = (typeData.types || []).filter(t => t.cost_per_hour > 0).map(t => ({ name: t.type_label, cph: t.cost_per_hour, spend: t.total_spend, life: t.avg_life_hrs }));

  return (
    <div data-testid="strata-comparison-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Ground Conditions & Type Analysis</h1>
          <p className="text-sm text-slate-500 mt-1">Compare tooth point performance by strata conditions and product type (casting vs forging)</p>
        </div>
      </div>

      <div className="flex gap-3 mb-6 flex-wrap items-end" data-testid="strata-filters">
        <CompanyFilter companies={companies} value={filterCompany} onChange={setFilterCompany} />
        <Select value={filterSite} onValueChange={setFilterSite}><SelectTrigger className="w-48 bg-white"><SelectValue placeholder="All Sites" /></SelectTrigger><SelectContent><SelectItem value="all">All Sites</SelectItem>{sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent></Select>
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} prefix="strata" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="strata" data-testid="tab-strata"><Layers size={14} className="mr-1.5" />Strata Comparison</TabsTrigger>
          <TabsTrigger value="type" data-testid="tab-type"><Hammer size={14} className="mr-1.5" />Casting vs Forging</TabsTrigger>
        </TabsList>

        <TabsContent value="strata">
          {loading ? <div className="animate-pulse h-48 bg-slate-100 rounded-lg" /> : (
            <>
              {strataChart.length > 0 && (
                <Card className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
                  <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Cost Per Operating Hour by Strata<MetricTip text="Cost/Hr = Total Spend / Total Operating Hours for all tooth point change-outs in each strata type. Lower is better." /></h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={strataChart}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748B' }} /><YAxis tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={v => `₹${v}`} /><RTooltip formatter={v => `₹${Number(v).toFixed(2)}`} contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12 }} /><Bar dataKey="cph" fill="#0F172A" radius={[4,4,0,0]} name="Cost/Hr" /></BarChart>
                  </ResponsiveContainer>
                </Card>
              )}
              <div className="data-table-wrapper">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50 border-b border-slate-200">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Strata Type</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Spend</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Qty Fitted</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Operating Hours</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Change-outs</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cost/Hr<MetricTip text="Total Spend / Total Operating Hours" /></TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cost/100 Hrs</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Confidence</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(strataData.strata || []).length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">No strata data recorded. Ensure strata is selected during operational data entry.</TableCell></TableRow> :
                    (strataData.strata || []).map((s, i) => (
                      <TableRow key={s.strata} className={`hover:bg-slate-50/50 ${i === 0 && s.cost_per_hour > 0 ? 'bg-emerald-50/30' : ''}`}>
                        <TableCell className="font-medium text-slate-900">{s.strata_label}</TableCell>
                        <TableCell className="text-sm font-medium">₹{s.total_spend.toLocaleString()}</TableCell>
                        <TableCell className="text-sm text-slate-600">{s.total_qty}</TableCell>
                        <TableCell className="text-sm text-slate-600">{s.total_hours.toLocaleString()}h</TableCell>
                        <TableCell className="text-sm text-slate-600">{s.change_outs}</TableCell>
                        <TableCell className="text-sm font-bold text-slate-900">{s.cost_per_hour > 0 ? `₹${s.cost_per_hour.toFixed(2)}` : '-'}</TableCell>
                        <TableCell className="text-sm">{s.cost_per_100hrs > 0 ? `₹${s.cost_per_100hrs.toFixed(2)}` : '-'}</TableCell>
                        <TableCell><ConfBadge level={s.confidence} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="type">
          {loading ? <div className="animate-pulse h-48 bg-slate-100 rounded-lg" /> : (
            <>
              <Card className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-amber-800"><strong>Note:</strong> This comparison reflects actual recorded performance data. Results depend on operating conditions, strata, and sample size. Neither casting nor forging is inherently superior; performance varies by application.</p>
              </Card>
              {typeChart.length > 0 && (
                <Card className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
                  <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Cost Per Operating Hour by Product Type<MetricTip text="Compares casting and forging tooth points by long-run cost per operating hour. Based on recorded change-out data only." /></h3>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={typeChart}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="name" tick={{ fontSize: 12, fill: '#334155' }} /><YAxis tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={v => `₹${v}`} /><RTooltip formatter={v => `₹${Number(v).toFixed(2)}`} contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12 }} /><Bar dataKey="cph" fill="#0F172A" radius={[4,4,0,0]} name="Cost/Hr" /></BarChart>
                  </ResponsiveContainer>
                </Card>
              )}
              <div className="data-table-wrapper">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50 border-b border-slate-200">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Product Type</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Spend</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Qty Fitted</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Operating Hours</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Change-outs</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cost/Hr</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Avg Life (Hrs)</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Confidence</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(typeData.types || []).length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">No type data. Ensure tooth point products have type (casting/forging) assigned.</TableCell></TableRow> :
                    (typeData.types || []).map(t => (
                      <TableRow key={t.type} className="hover:bg-slate-50/50">
                        <TableCell><span className={`text-xs font-semibold uppercase tracking-wider px-2 py-1 rounded ${t.type === 'forging' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-600'}`}>{t.type_label}</span></TableCell>
                        <TableCell className="text-sm font-medium">₹{t.total_spend.toLocaleString()}</TableCell>
                        <TableCell className="text-sm text-slate-600">{t.total_qty}</TableCell>
                        <TableCell className="text-sm text-slate-600">{t.total_hours.toLocaleString()}h</TableCell>
                        <TableCell className="text-sm text-slate-600">{t.change_outs}</TableCell>
                        <TableCell className="text-sm font-bold text-slate-900">{t.cost_per_hour > 0 ? `₹${t.cost_per_hour.toFixed(2)}` : '-'}</TableCell>
                        <TableCell className="text-sm text-slate-600">{t.avg_life_hrs > 0 ? `${t.avg_life_hrs.toFixed(0)}h` : '-'}</TableCell>
                        <TableCell><ConfBadge level={t.confidence} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
